import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Clock, TrendingUp, Crown, Heart, Ticket, ExternalLink, Navigation, Bookmark, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { EventCountdown } from '@/components/EventCountdown';
import { SocialShare } from '@/components/SocialShare';
import { FriendsAttending } from '@/components/FriendsAttending';
import { OrangeStreetTiers } from '@/components/OrangeStreetTiers';
import { fetchEventBySlug, fetchSimilarEvents } from '@/lib/events';
import { adaptEvent, adaptEvents } from '@/lib/eventAdapter';
import { usePageMeta } from '@/hooks/usePageMeta';
import { NotFound } from '@/pages/NotFound';
import { useAuth } from '@/contexts/AuthContext';
import { myEvents } from '@/lib/myEvents';

// Orange Street is canonical backend event id "7" (Phase 10C/10D); Eventbrite-only.
const ORANGE_STREET_BACKEND_ID = '7';
const ORANGE_STREET_SLUG = 'orange-street-live-in-new-jersey';

export const EventDetail = () => {
  const { id: slugParam } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [dj] = useState(null);          // backend events carry no linked mock DJ
  const [notFound, setNotFound] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const { isAuthenticated } = useAuth();
  const [myStatus, setMyStatus] = useState(null);   // null | 'saved' | 'attending'
  const [savingMy, setSavingMy] = useState(false);
  const [similar, setSimilar] = useState([]);       // Version 1 Similar Events (may be [])
  const [similarLoaded, setSimilarLoaded] = useState(false);
  const [activePoster, setActivePoster] = useState(0);   // poster gallery index

  usePageMeta(
    event ? event.title : 'Event',
    event ? event.description : 'View event details, lineup and tickets on SoundWolves.',
    { image: event && event.image && event.image.startsWith('http') ? event.image : undefined },
  );

  useEffect(() => {
    let active = true;
    // Legacy numeric URLs (e.g. /event/16 or /event/7) -> redirect to canonical slug.
    if (/^\d+$/.test(slugParam)) {
      if (slugParam === '16' || slugParam === ORANGE_STREET_BACKEND_ID) {
        navigate(`/event/${ORANGE_STREET_SLUG}`, { replace: true });
      } else {
        navigate('/events', { replace: true });
      }
      return;
    }
    setNotFound(false);
    setEvent(null);
    fetchEventBySlug(slugParam)
      .then((data) => {
        if (!active) return;
        const adapted = adaptEvent(data);
        setEvent(adapted);
        const favorites = JSON.parse(localStorage.getItem('soundwolves_favorite_events') || '[]');
        setIsFavorited(!!adapted && favorites.includes(adapted.id));
      })
      .catch(() => { if (active) setNotFound(true); });   // 404 / API error -> Not Found
    return () => { active = false; };
  }, [slugParam, navigate]);

  // Reflect the current My Events state for this event (logged-in users only).
  useEffect(() => {
    if (!isAuthenticated || !event?.id) { setMyStatus(null); return; }
    let active = true;
    myEvents.list()
      .then((list) => {
        if (!active) return;
        const m = (list || []).find((i) => String(i.event_id) === String(event.id));
        setMyStatus(m ? m.status : null);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [isAuthenticated, event?.id]);

  // Similar Events (Version 1, rule-based, read-only). An empty list or any API
  // error resolves to the "More events coming soon" fallback — never an error
  // state and never fabricated/padded results.
  useEffect(() => {
    if (!event?.slug) { setSimilar([]); setSimilarLoaded(false); return; }
    let active = true;
    setSimilarLoaded(false);
    fetchSimilarEvents(event.slug, 6)
      .then((list) => { if (active) { setSimilar(adaptEvents(list)); setSimilarLoaded(true); } })
      .catch(() => { if (active) { setSimilar([]); setSimilarLoaded(true); } });
    return () => { active = false; };
  }, [event?.slug]);

  if (notFound) {
    return (
      <NotFound
        title="Event Not Found"
        message="This event doesn't exist or has been removed. Browse our upcoming events instead."
      />
    );
  }

  const handleGetTickets = () => {
    if (event?.ticketLink) {
      window.open(event.ticketLink, '_blank');
      toast.success('Redirecting to ticketing partner...');
    }
  };

  // My Events: save / confirm attendance. Confirming drives the sample ticket
  // that appears under My Tickets. No payment, order, or admission credential.
  const handleSaveToggle = async () => {
    if (!isAuthenticated) { toast.info('Log in to save events'); navigate('/login'); return; }
    setSavingMy(true);
    try {
      if (myStatus) {
        await myEvents.remove(event.id);
        setMyStatus(null);
        toast.info('Removed from My Events');
      } else {
        await myEvents.add(event.id);
        setMyStatus('saved');
        toast.success('Saved to My Events');
      }
    } catch (e) {
      toast.error(e.message || 'Could not update My Events');
    } finally {
      setSavingMy(false);
    }
  };

  const handleAttend = async () => {
    if (!isAuthenticated) { toast.info('Log in to RSVP'); navigate('/login'); return; }
    setSavingMy(true);
    try {
      await myEvents.confirm(event.id);
      setMyStatus('attending');
      toast.success("You're attending — a sample ticket is now in My Tickets.");
    } catch (e) {
      toast.error(e.message || 'Could not confirm attendance');
    } finally {
      setSavingMy(false);
    }
  };

  const toggleFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem('soundwolves_favorite_events') || '[]');
    const eventId = event?.id;
    if (!eventId) return;

    let newFavorites;
    if (isFavorited) {
      newFavorites = favorites.filter(fav => fav !== eventId);
      toast.info('Removed from favorites');
    } else {
      newFavorites = [...favorites, eventId];
      toast.success('Added to favorites!');
    }

    localStorage.setItem('soundwolves_favorite_events', JSON.stringify(newFavorites));
    setIsFavorited(!isFavorited);
  };

  const openMaps = () => {
    if (event?.address) {
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address)}`;
      window.open(mapsUrl, '_blank');
    }
  };

  if (!event) {
    return (
      <div className="min-h-screen pt-20 pb-24 md:pb-8 flex items-center justify-center">
        <p className="text-muted-foreground">Loading event details...</p>
      </div>
    );
  }

  // Poster gallery: DJ events carry posters[] (1–3); other events fall back to the
  // single hero image. activePoster indexes the gallery for the carousel.
  const gallery = (event.posters && event.posters.length > 0) ? event.posters : [event.image];
  const heroSrc = gallery[Math.min(activePoster, gallery.length - 1)] || event.image;

  return (
    <div className="min-h-screen pt-20 pb-24 md:pb-8">
      {/* Hero Image / Poster Gallery */}
      <div className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <img
          src={heroSrc}
          alt={event.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />

        {/* Poster thumbnails (only when there is more than one poster) */}
        {gallery.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {gallery.map((src, i) => (
              <button
                key={src + i}
                type="button"
                onClick={() => setActivePoster(i)}
                className={`h-14 w-14 rounded-md overflow-hidden border-2 transition-all ${i === activePoster ? 'border-primary scale-105' : 'border-white/40 opacity-80 hover:opacity-100'}`}
                aria-label={`View poster ${i + 1}`}
              >
                <img src={src} alt={`${event.title} poster ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
        
        {/* Floating Action Buttons */}
        <div className="absolute top-4 right-4 flex gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="glass-card hover:bg-background/50"
            onClick={toggleFavorite}
          >
            <Heart className={`w-5 h-5 ${isFavorited ? 'fill-destructive text-destructive' : ''}`} />
          </Button>
          <SocialShare 
            title={`${event.title} - SOUNDWOLVES`}
            description={event.description}
          />
        </div>

        {/* Badges */}
        <div className="absolute top-4 left-4 flex flex-wrap gap-2">
          {event.trending && (
            <Badge className="bg-destructive/90 text-white backdrop-blur-sm">
              <TrendingUp className="w-3 h-3 mr-1" />
              Trending
            </Badge>
          )}
          {event.tags.slice(0, 2).map((tag) => (
            <Badge key={tag} className="bg-primary/90 text-primary-foreground backdrop-blur-sm">
              <Crown className="w-3 h-3 mr-1" />
              {tag}
            </Badge>
          ))}
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-border/50">
              <CardContent className="p-6 sm:p-8">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4">
                  {event.title}
                </h1>
                
                <div className="flex items-center gap-2 mb-6">
                  {dj ? (
                    <>
                      <Link to={`/dj/${event.artistId}`}>
                        <Button variant="link" className="p-0 h-auto text-lg text-primary hover:text-primary/80">
                          by {event.artist}
                        </Button>
                      </Link>
                      {dj.verified && (
                        <Badge variant="secondary" className="text-xs">Verified</Badge>
                      )}
                    </>
                  ) : (
                    <p className="text-lg text-muted-foreground">by {event.artist}</p>
                  )}
                </div>

                {/* Event Countdown */}
                <div className="mb-6">
                  <EventCountdown dateISO={event.dateISO} eventTitle={event.title} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Date</p>
                      <p className="font-semibold">{event.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Time</p>
                      <p className="font-semibold">{event.time} - {event.endTime}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 col-span-2 sm:col-span-1">
                    <MapPin className="w-5 h-5 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">Venue</p>
                      <p className="font-semibold">{event.venue}</p>
                      <p className="text-xs text-muted-foreground">{event.city}</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={openMaps}>
                      <Navigation className="w-4 h-4" />
                    </Button>
                  </div>
                  {event.attendees != null && event.capacity != null && (
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Attending</p>
                        <p className="font-semibold">{event.attendees} / {event.capacity}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Age Restriction Badge */}
                {event.ageRestriction && (
                  <Badge variant="outline" className="mb-6">
                    {event.ageRestriction}
                  </Badge>
                )}

                <Separator className="my-6" />

                {/* Friends Attending — only when there is real social data */}
                {event.friendsAttending && event.friendsAttending.length > 0 && (
                  <>
                    <div className="mb-6">
                      <FriendsAttending
                        friendsAttending={event.friendsAttending}
                        eventTitle={event.title}
                      />
                    </div>
                    <Separator className="my-6" />
                  </>
                )}

                <div>
                  <h2 className="text-2xl font-display font-bold mb-4">About This Event</h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    {event.description}
                  </p>
                </div>

                {event.highlights.length > 0 && (
                  <div>
                    <h3 className="text-xl font-display font-bold mb-4">Event Highlights</h3>
                    <ul className="space-y-3">
                      {event.highlights.map((highlight, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <div className="w-2 h-2 rounded-full bg-primary" />
                          </div>
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {event.whatToExpect && event.whatToExpect.length > 0 && (
                  <div>
                    <h3 className="text-xl font-display font-bold mb-4">What to Expect</h3>
                    <ul className="space-y-3">
                      {event.whatToExpect.map((item, index) => (
                        <li key={index} className="text-muted-foreground leading-relaxed">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* DJ Info Card */}
            {dj && (
              <Card className="border-border/50">
                <CardContent className="p-6">
                  <h3 className="text-xl font-display font-bold mb-4">About the DJ</h3>
                  <Link to={`/dj/${dj.id}`}>
                    <div className="flex items-center gap-4 p-4 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                      <div className="w-16 h-16 rounded-xl overflow-hidden">
                        <img src={dj.image} alt={dj.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg">{dj.name}</h4>
                        <p className="text-sm text-muted-foreground">{dj.specialty}</p>
                        <div className="flex items-center gap-3 mt-1 text-sm">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {dj.followers}
                          </span>
                          <span className="flex items-center gap-1">
                            <Badge variant="secondary" className="text-xs">
                              ⭐ {dj.rating}
                            </Badge>
                          </span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">View Profile</Button>
                    </div>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Ticket Booking */}
          <div className="lg:col-span-1">
            <Card className="border-border/50 sticky top-24">
              <CardContent className="p-6 space-y-6">
                {String(event.id) === ORANGE_STREET_BACKEND_ID ? (
                  <div>
                    <h3 className="text-2xl font-display font-bold mb-4">Get Your Tickets</h3>
                    <OrangeStreetTiers />
                  </div>
                ) : (
                  <>
                    <div>
                      <h3 className="text-2xl font-display font-bold mb-4">Get Your Tickets</h3>

                      <div className="space-y-4">
                        <div className="p-4 rounded-lg bg-muted/50 border border-border">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-muted-foreground">{event.isFree ? 'Admission' : 'General Admission'}</span>
                            <Badge variant="secondary">{event.isFree ? 'Free' : 'Available'}</Badge>
                          </div>
                          <p className="text-3xl font-bold text-primary">
                            {event.isPaid && event.priceNum != null ? `Starts from ${event.price}` : event.price}
                          </p>
                        </div>

                        {/* VIP tier only when a real VIP price exists. */}
                        {event.vipPriceNum != null && (
                          <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm flex items-center gap-2">
                                <Crown className="w-4 h-4 text-primary" />
                                VIP Experience
                              </span>
                              <Badge className="bg-primary/20 text-primary border-primary/30">Limited</Badge>
                            </div>
                            <p className="text-3xl font-bold text-primary">{event.vipPrice}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Buy Tickets only when there's a ticket/payment link (paid events). */}
                    {event.ticketLink ? (
                      <>
                        <Button
                          variant="premium"
                          size="lg"
                          className="w-full"
                          onClick={handleGetTickets}
                        >
                          <Ticket className="w-5 h-5 mr-2" />
                          Buy Tickets
                          <ExternalLink className="w-4 h-4 ml-2" />
                        </Button>

                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">
                            Powered by our ticketing partners
                          </p>
                        </div>
                      </>
                    ) : event.isFree ? (
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground">This is a free event — no ticket purchase needed.</p>
                      </div>
                    ) : null}
                  </>
                )}

                <Separator />

                {/* My Events — save / confirm attendance (drives My Tickets sample ticket) */}
                <div className="space-y-2">
                  <Button
                    variant={myStatus ? 'outline' : 'secondary'}
                    className="w-full"
                    disabled={savingMy}
                    onClick={handleSaveToggle}
                    data-testid="myevents-save-btn"
                  >
                    <Bookmark className={`w-4 h-4 mr-2 ${myStatus ? 'fill-current' : ''}`} />
                    {myStatus ? 'Saved to My Events' : 'Save to My Events'}
                  </Button>
                  {myStatus === 'attending' ? (
                    <div className="flex items-center justify-center gap-2 text-sm text-green-500 py-1">
                      <CheckCircle2 className="w-4 h-4" /> You’re attending — sample ticket in My Tickets
                    </div>
                  ) : (
                    <Button
                      variant="premium"
                      className="w-full"
                      disabled={savingMy}
                      onClick={handleAttend}
                      data-testid="myevents-attend-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" /> I’m attending
                    </Button>
                  )}
                </div>

                <Separator />

                <div>
                  <h4 className="font-semibold mb-3">Share this event</h4>
                  <SocialShare 
                    title={`${event.title} - SOUNDWOLVES`}
                    description={event.description}
                    variant="buttons"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Similar Events (Version 1, rule-based). Shows up to 6 public events;
            an empty result renders the honest "More events coming soon" fallback. */}
        {similarLoaded && (
          <div className="mt-12">
            <h2 className="text-2xl font-display font-bold mb-6">Similar Events</h2>
            {similar.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {similar.map((s) => (
                  <Link key={s.id} to={`/event/${s.slug}`}>
                    <Card className="group overflow-hidden border-border/50 hover-lift cursor-pointer h-full">
                      <div className="relative h-36 overflow-hidden">
                        <img
                          src={s.image}
                          alt={s.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
                        {s.category && (
                          <div className="absolute top-2 left-2">
                            <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm text-xs">
                              {s.category}
                            </Badge>
                          </div>
                        )}
                      </div>
                      <CardContent className="p-3 space-y-2">
                        <h3 className="font-display font-bold text-sm group-hover:text-primary transition-colors line-clamp-1">
                          {s.title}
                        </h3>
                        {s.venue && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="w-3 h-3" />
                            <span className="truncate">{s.venue}</span>
                          </div>
                        )}
                        {s.date && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Calendar className="w-3 h-3" />
                            <span>{s.date}</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">More events coming soon</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetail;
